import { useMutation, useQuery } from 'react-query';
import { api } from '../../../../../services/api';
import { Box, Button, Text, Tooltip, useToast } from '@chakra-ui/react';
import { MenuCategoria } from './MenuCategoria';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import 'quill-paste-smart';
import { wordsLength } from '../../../../../utils/wordsLength';
import { countWords } from '../../../../../utils/countWords';

const modules = {
  toolbar: ['bold', 'italic', 'underline', 'link', 'clean']
};

const formats = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'header',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  'formula',
  'image',
  'video',
  'br'
];

Quill.register('modules/formats', formats);

const schema = z.object({
  categoria: z.object({
    codigo_grupo: z.string(),
    descricao_categoria: z.string()
  }),
  conteudo: z.string().refine((value) => wordsLength(value), {
    message: 'O texto deve conter entre 500 e 1000 palavras'
  })
});

async function getTextoSeoRegistered(categoryId) {
  const response = await api.get(
    `/site/v2/atualizacao-seo-categoria/listar-texto-grupo/${categoryId}`
  );

  return response.data;
}

async function updateCategoryText(data) {
  const response = await api.post('/site/v2/atualizacao-seo-categoria/atualiza-texto-grupo', data);

  return response.data;
}

export function TextoCategoria() {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue
  } = useForm({
    resolver: zodResolver(schema)
  });

  const categorySelected = watch('categoria');
  const conteudo = watch('conteudo');

  const wordCount = countWords(conteudo);

  const toast = useToast({
    position: 'top-right'
  });

  const { data: registeredCategory, isLoading: isLoadingRegisteredCategory } = useQuery({
    queryKey: ['registeredCategory', categorySelected?.codigo_grupo],
    queryFn: () => getTextoSeoRegistered(categorySelected?.codigo_grupo),
    refetchOnWindowFocus: false,
    enabled: !!categorySelected,
    onSuccess: (data) => {
      setValue('conteudo', data[0].texto_categoria);
    }
  });

  const { mutateAsync, isLoading: isSubmittingLoading } = useMutation({
    mutationFn: updateCategoryText,
    onError: (error, variables, context) => {
      console.log('onError');
      toast({
        title: 'Erro ao atualizar texto da categoria',
        status: 'error',
        duration: 2000,
        isClosable: true
      });
    },
    onSuccess: (data, variables, context) => {
      console.log('onSuccess');
      toast({
        title: 'Texto da categoria atualizado com sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    }
  });

  async function handleUpdateCategoryText(data) {
    try {
      await mutateAsync(data);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <Box
      as="form"
      w="100%"
      onSubmit={handleSubmit(handleUpdateCategoryText)}
      display="flex"
      flexDirection="column"
      alignItems="start"
      justifyContent="center"
      gap={4}>
      <Controller
        name="categoria"
        control={control}
        render={({ field }) => (
          <MenuCategoria
            onSelectedCategory={field.onChange}
            category={field.value?.descricao_categoria}
          />
        )}
      />

      {categorySelected && (
        <Text fontSize="sm" color="gray.500">
          Você está alterando o texto da categoria: {categorySelected.descricao_categoria}
        </Text>
      )}

      {wordCount && (
        <Text fontSize="sm" color="gray.500">
          {wordCount} palavras
        </Text>
      )}

      {errors.conteudo && (
        <Text fontSize="sm" color="red.500">
          {errors.conteudo.message}
        </Text>
      )}
      <Controller
        control={control}
        defaultValue=""
        rules={{ required: 'Campo obrigatório' }}
        name="conteudo"
        render={({ field }) => (
          <ReactQuill
            theme="snow"
            {...field}
            modules={modules}
            formats={formats}
            placeholder="Digite o texto da categoria"
            id="editor"
            style={{ height: '300px', width: '100%' }}
            //desabilita se nao tiver categoria selecionada
            readOnly={!categorySelected}
          />
        )}
      />

      <Button
        colorScheme="blue"
        type="submit"
        disabled={!categorySelected}
        mt={10}
        isLoading={isSubmittingLoading}>
        Salvar
      </Button>
    </Box>
  );
}
